import React, { useState } from 'react';
import { Reorder, useDragControls } from 'framer-motion';
import { GripVerticalIcon } from 'lucide-react';
import { usePortal } from '../../contexts/PortalContext';
import { DELIVERABLE_STATUSES, DELIVERABLE_TYPES } from '../../types/portal';
import type { Deliverable } from '../../types/portal';
import { deliverableStage, isVersioned, metaLabel, stageHeading } from '../../utils/deliverables';
import { VersionHistory } from './VersionHistory';
import { TranscriptUpload } from './TranscriptUpload';
import { TranscriptRevisions } from './TranscriptRevisions';
import { isDriveFolder, toDrivePreviewUrl } from '../../utils/drive';
import { BUTTON_GHOST, BUTTON_QUIET, BUTTON_SOLID, FIELD, OPTION, SELECT } from './adminStyles';
interface DeliverableAdminProps {
  deliverable: Deliverable;
  /** Reports which row is currently lifted, or null once it is set down. */
  onLift: (id: string | null) => void;
}
export function DeliverableAdmin({
  deliverable,
  onLift
}: DeliverableAdminProps) {
  const dragControls = useDragControls();
  const [carrying, setCarrying] = useState(false);
  const {
    updateDeliverable,
    removeDeliverable,
    publishVersion,
    resolveChangeRequest,
    notify,
    focusDeliverableId
  } = usePortal();

  /** 'details' shows the client feedback; 'edit' also shows the configuration. */
  const [panel, setPanel] = useState<'closed' | 'details' | 'edit'>(focusDeliverableId === deliverable.id ? 'details' : 'closed');
  const open = panel !== 'closed';
  const editingRow = panel === 'edit';
  const setOpen = (next: boolean) => setPanel(next ? 'edit' : 'closed');
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [draft, setDraft] = useState<Deliverable>(deliverable);
  function edit(patch: Partial<Deliverable>) {
    setDraft(current => ({
      ...current,
      ...patch
    }));
  }
  function save() {
    updateDeliverable(deliverable.id, {
      name: draft.name.trim() || deliverable.name,
      type: draft.type,
      quantity: draft.quantity,
      status: draft.status,
      version: draft.version,
      driveUrl: draft.driveUrl,
      audioUrl: draft.audioUrl,
      internalNote: draft.internalNote,
      visibleToClient: draft.visibleToClient,
      transcript: draft.transcript
    });
    notify(`${draft.name || deliverable.name} · client view updated`);
    setOpen(false);
  }
  function publish() {
    updateDeliverable(deliverable.id, {
      driveUrl: draft.driveUrl,
      audioUrl: draft.audioUrl
    });
    publishVersion(deliverable.id, draft.version);
    notify(`${deliverable.name} · ${draft.version || 'new version'} published for review`);
  }
  const outstanding = deliverable.changeRequests.filter(request => !request.resolved);
  /** Completed requests stay on the record, newest first. */
  const completed = deliverable.changeRequests.filter(request => request.resolved).reverse();
  return <Reorder.Item value={deliverable} dragListener={false} dragControls={dragControls} onDragStart={() => {
    setCarrying(true);
    onLift(deliverable.id);
  }} onDragEnd={() => {
    setCarrying(false);
    onLift(null);
  }}
  /* The row itself is carried, so it stays fully visible and readable. */ animate={carrying ? {
    backgroundColor: 'rgba(255,255,255,0.05)',
    boxShadow: '0 18px 40px rgba(0,0,0,0.55)'
  } : {
    backgroundColor: 'rgba(255,255,255,0)',
    boxShadow: '0 0 0 rgba(0,0,0,0)'
  }} transition={{
    duration: 0.18,
    ease: [0.23, 1, 0.32, 1]
  }} style={{
    position: 'relative',
    zIndex: carrying ? 30 : 0
  }} className={['border-b border-ruleDark', carrying ? 'cursor-grabbing rounded-[2px] ring-1 ring-paper/25' : ''].join(' ')} data-id="element-179" data-mp="7gdlp3ewnwi5">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-4 px-3 py-6" data-id="element-180" data-mp="2tn8cgmk2gypa">
        <span role="button" tabIndex={0} onPointerDown={event => {
        event.preventDefault();
        dragControls.start(event);
      }} aria-label={`Reorder ${deliverable.name}`} title="Drag to reorder" className="shrink-0 touch-none cursor-grab text-paper/30 transition-colors duration-150 ease-out hover:text-paper/70 active:cursor-grabbing" data-id="element-181" data-mp="2ooopqjxnfivv">
          <GripVerticalIcon className="h-5 w-5" strokeWidth={1.25} data-id="element-182" data-mp="1hbe5lnhsl2z4" />
        </span>

        {/* The row itself opens the deliverable's details and client feedback. */}
        <button type="button" onClick={() => setPanel(current => current === 'details' ? 'closed' : 'details')} aria-expanded={open} className="min-w-0 flex-1 text-left" data-id="element-183" data-mp="17f1gz413tfb8">
          <p className="font-display text-2xl leading-none sm:text-3xl" data-id="element-184" data-mp="ymfxflpnf4lc">{deliverable.name}</p>
          <p className="label-sm mt-3 text-paper/40" data-id="element-185" data-mp="jpgsy6q3frv3">
            {metaLabel(deliverable)} · {stageHeading(deliverableStage(deliverable.status))} ·{' '}
            {deliverable.status}
            {outstanding.length > 0 ? ` · ${outstanding.length} change${outstanding.length === 1 ? '' : 's'} requested` : ''}
          </p>
        </button>

        <button type="button" onClick={() => {
        updateDeliverable(deliverable.id, {
          visibleToClient: !deliverable.visibleToClient
        });
        edit({
          visibleToClient: !deliverable.visibleToClient
        });
      }} aria-pressed={deliverable.visibleToClient} className={['label shrink-0 border px-3 py-2 transition-colors duration-150 ease-out', deliverable.visibleToClient ? 'border-paper bg-paper text-ink' : 'border-paper/30 text-paper/40 hover:border-paper/60'].join(' ')} data-id="element-186" data-mp="f1fr7i0eex0y">
          {deliverable.visibleToClient ? 'Visible' : 'Hidden'}
        </button>

        <div className="flex shrink-0 items-center gap-6" data-id="element-187" data-mp="12qihfe93ly72">
          <button type="button" onClick={() => {
          setDraft(deliverable);
          setPanel(current => current === 'edit' ? 'closed' : 'edit');
          setConfirmRemove(false);
        }} className={BUTTON_QUIET} data-id="element-188" data-mp="12nw1orc20cdr">
            {editingRow ? 'Close' : 'Edit'}
          </button>
          <button type="button" onClick={() => setConfirmRemove(true)} className="label text-paper/40 transition-colors duration-150 ease-out hover:text-paper" data-id="element-189" data-mp="1oicwkcluau6y">
            Remove
          </button>
        </div>
      </div>

      {confirmRemove && <div className="mb-6 border border-paper/30 px-6 py-6" data-id="element-190" data-mp="22hksmwmphm6x">
          <p className="font-display text-xl leading-snug" data-id="element-191" data-mp="27gxfvihhova1">
            Remove {deliverable.name} from the dashboard?
          </p>
          <p className="label-sm mt-3 text-paper/40" data-id="element-192" data-mp="84mpqy3c5rtm">
            This only removes it from the client dashboard. Nothing is deleted from Google Drive.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-6" data-id="element-193" data-mp="5jexvlclxkdx">
            <button type="button" onClick={() => {
          removeDeliverable(deliverable.id);
          notify(`${deliverable.name} removed from dashboard`);
        }} className={BUTTON_SOLID} data-id="element-194" data-mp="5kpzbeikyg1v">
              Remove
            </button>
            <button type="button" onClick={() => setConfirmRemove(false)} className={BUTTON_QUIET} data-id="element-195" data-mp="3mxsfjngggmvg">
              Cancel
            </button>
          </div>
        </div>}

      {open && <div className="mb-10 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-16" data-id="element-196" data-mp="jh3gsnn46hgc">
          {editingRow && <div className="lg:col-span-7" data-id="element-197" data-mp="elwjbxkfu8zv">
            <label className="block" data-id="element-198" data-mp="9ls03zs5b05y">
              <span className="label block text-paper/40" data-id="element-199" data-mp="9kqre1ylqpre">Name</span>
              <input value={draft.name} onChange={event => edit({
            name: event.target.value
          })} placeholder="e.g. Event Highlights Film" className={FIELD} data-id="element-200" data-mp="2zbdh06hzk3bk" />
            </label>

            <div className="mt-8 grid grid-cols-2 gap-8 sm:grid-cols-3" data-id="element-201" data-mp="3oygb3blkm9gt">
              <label className="block" data-id="element-202" data-mp="3ozra6y42mt1l">
                <span className="label block text-paper/40" data-id="element-203" data-mp="3az6tq8lmp9b9">Type</span>
                <select value={draft.type} onChange={event => edit({
              type: event.target.value as Deliverable['type']
            })} className={SELECT} data-id="element-204" data-mp="3b11enmwwh1fc">
                  {DELIVERABLE_TYPES.map(option => <option key={option} value={option} className={OPTION} data-id="element-205" data-mp="bj1vmmc2kpn1">
                      {option}
                    </option>)}
                </select>
              </label>
              <label className="block" data-id="element-206" data-mp="1s735088ctt5">
                <span className="label block text-paper/40" data-id="element-207" data-mp="pjkyzg5lb7iv">Quantity</span>
                <input value={draft.quantity} onChange={event => edit({
              quantity: event.target.value
            })} placeholder="e.g. 8" className={FIELD} data-id="element-208" data-mp="ph882dhutmto" />
              </label>
              {isVersioned(draft.status) ? <label className="block" data-id="element-209" data-mp="29zj1r00z6v9m">
                  <span className="label block text-paper/40" data-id="element-210" data-mp="2oqjaz79jqt6t">Version</span>
                  <input value={draft.version} onChange={event => edit({
              version: event.target.value
            })} placeholder="e.g. V3" className={FIELD} data-id="element-211" data-mp="2jrgfoejdc1c1" />
                </label> : <div className="block" data-id="element-212" data-mp="25zs2s91ap8u0">
                  <span className="label block text-paper/40" data-id="element-213" data-mp="260tdudib9vbq">Version</span>
                  <p className="mt-3 border-b border-paper/25 pb-2 font-sans text-sm tracking-wide2 text-paper/30" data-id="element-214" data-mp="2105ri55122nu">
                    Added once In Review
                  </p>
                </div>}
            </div>

            <label className="mt-8 block" data-id="element-215" data-mp="3cf2i3jgv0zxb">
              <span className="label block text-paper/40" data-id="element-216" data-mp="wdc727canvru">Status</span>
              <select value={draft.status} onChange={event => edit({
            status: event.target.value as Deliverable['status']
          })} className={SELECT} data-id="element-217" data-mp="wciweva7sm4f">
                {DELIVERABLE_STATUSES.map(option => <option key={option} value={option} className={OPTION} data-id="element-218" data-mp="hneezgb8ulxd">
                    {option}
                  </option>)}
              </select>
              {/* Stage follows the status, so there is nothing else to set. */}
              <span className="label-sm mt-3 block text-paper/30" data-id="element-219" data-mp="8q40lm34qgo8">
                Stage · {stageHeading(deliverableStage(draft.status))} · set from status
              </span>
            </label>

            {draft.type === 'Transcript' && <TranscriptUpload value={draft.transcript ?? ''} onChange={text => edit({
          transcript: text
        })} data-id="element-220" data-mp="1y1moa9706yr2" />}

            <label className="mt-8 block" data-id="element-221" data-mp="20ndku0ydfy2u">
              <span className="label block text-paper/40" data-id="element-222" data-mp="20mkoltx1gqyy">Google Drive Link</span>
              <input value={draft.driveUrl} onChange={event => edit({
            driveUrl: event.target.value
          })} placeholder="Paste Drive file or folder link" className={FIELD} data-id="element-223" data-mp="25l5dwyar5q8m" />
              {draft.driveUrl.trim() && <span className="label-sm mt-3 block text-paper/30" data-id="element-224" data-mp="1hp1eemyuh2ay">
                  {toDrivePreviewUrl(draft.driveUrl) ? `Embeds as ${toDrivePreviewUrl(draft.driveUrl)}` : isDriveFolder(draft.driveUrl) ? 'Folder link · opens in Drive, cannot play inside Portal' : 'Link not recognised as a Drive file — the client will see a preview-unavailable message'}
                </span>}
            </label>

            <label className="mt-8 block" data-id="element-225" data-mp="37zw3vrbzji0c">
              <span className="label block text-paper/40" data-id="element-226" data-mp="382s5pqqyequ2">Drive Audio Link (optional)</span>
              <input value={draft.audioUrl} onChange={event => edit({
            audioUrl: event.target.value
          })} placeholder="Paste Drive audio link" className={FIELD} data-id="element-227" data-mp="17wla9atyxko2" />
            </label>

            <label className="mt-8 block" data-id="element-228" data-mp="1cx99ukw9j9tw">
              <span className="label block text-paper/40" data-id="element-229" data-mp="1cvem6ytlx3m4">Internal Note</span>
              <input value={draft.internalNote} onChange={event => edit({
            internalNote: event.target.value
          })} placeholder="Portal only — never shown to the client" className={FIELD} data-id="element-230" data-mp="p8g98b4p4dsy" />
            </label>

            <label className="mt-8 flex items-center justify-between gap-6 border-b border-paper/25 pb-3" data-id="element-231" data-mp="t8gxenhlk3d7">
              <span className="label text-paper/40" data-id="element-232" data-mp="5imlc853qiu0">Visible to Client</span>
              <button type="button" onClick={() => edit({
            visibleToClient: !draft.visibleToClient
          })} aria-pressed={draft.visibleToClient} className={['label border px-3 py-2 transition-colors duration-150 ease-out', draft.visibleToClient ? 'border-paper bg-paper text-ink' : 'border-paper/30 text-paper/40 hover:border-paper/60'].join(' ')} data-id="element-233" data-mp="1j5hfj5bptqy">
                {draft.visibleToClient ? 'On' : 'Off'}
              </button>
            </label>

            <div className="mt-10 flex flex-wrap items-center gap-5" data-id="element-234" data-mp="f2yfl5itfw3q">
              <button type="button" onClick={save} className={BUTTON_SOLID} data-id="element-235" data-mp="78t769pxgyh">
                Update Client View
              </button>
              <button type="button" onClick={publish} className={BUTTON_GHOST} data-id="element-236" data-mp="54iov3npaour">
                Publish New Version
              </button>
            </div>
            <p className="label-sm mt-6 max-w-md leading-relaxed text-paper/30" data-id="element-237" data-mp="2pvt0ze23s7p0">
              Publishing sets the client status to Ready for Review, keeps all notes and adds a
              system note to the conversation.
            </p>
          </div>}

          <div className={editingRow ? 'lg:col-span-5 lg:border-l lg:border-ruleDark lg:pl-12' : 'lg:col-span-7'} data-id="element-238" data-mp="31ae5zma7ksw2">
            {/* Outstanding feedback leads, so nothing needing action is buried. */}
            {outstanding.length === 0 && (deliverable.type === 'Transcript' ? <TranscriptRevisions deliverable={deliverable} data-id="element-239" data-mp="3k3i0taz5xbs9" /> : <VersionHistory deliverable={deliverable} data-id="element-240" data-mp="3f2kk72tswpsv" />)}

            <h4 className={`label border-b border-ruleDark pb-4 text-paper/40 ${outstanding.length > 0 ? '' : 'mt-12'}`} data-id="element-241" data-mp="3a5aq39vgvpyp">
              Requested Changes
            </h4>
            <ul className="m-0 list-none p-0" data-id="element-242" data-mp="1yo2i6elm3rps">
              {outstanding.length === 0 && <li className="py-5 font-display text-lg text-paper/40" data-id="element-243" data-mp="e8w9g146dtn7">Nothing outstanding.</li>}
              {outstanding.map(request => <li key={request.id} className="border-b border-ruleDark py-5" data-id="element-244" data-mp="o60zic15699a">
                  <span className="label block text-paper" data-id="element-245" data-mp="o3y39a9ebjw4">
                    {request.timecode ? `${request.timecode} · ` : ''}
                    {request.authorName}
                  </span>
                  <p className="mt-2 font-display text-lg leading-snug" data-id="element-246" data-mp="xxwwtkvelvcg">{request.body}</p>
                  <div className="mt-3 flex items-center justify-between gap-4" data-id="element-247" data-mp="11z8pjz9kqwov">
                    <span className="label-sm text-paper/35" data-id="element-248" data-mp="16x1wwvmm5wqq">
                      {request.version ? `${request.version} · ` : ''}
                      Sent {request.time || request.submitted}
                    </span>
                    <button type="button" onClick={() => {
                resolveChangeRequest(deliverable.id, request.id);
                notify('Marked as completed');
              }} className="label-sm shrink-0 text-paper/60 underline underline-offset-4 transition-colors duration-150 ease-out hover:text-paper" data-id="element-249" data-mp="38pyivdi2ajpm">
                      Mark completed
                    </button>
                  </div>
                </li>)}
            </ul>

            {completed.length > 0 && <>
                <h4 className="label mt-12 border-b border-ruleDark pb-4 text-paper/40" data-id="element-250" data-mp="3kjt70e03ubnb">
                  Completed Changes
                </h4>
                <ul className="m-0 list-none p-0" data-id="element-251" data-mp="24ij9davie86">
                  {completed.map(request => <li key={request.id} className="border-b border-ruleDark py-5" data-id="element-252" data-mp="3tcd61fkb6i8o">
                      <span className="label block text-paper/60" data-id="element-253" data-mp="c3znrree2m2k">
                        {request.timecode ? `${request.timecode} · ` : ''}
                        {request.authorName}
                      </span>
                      <p className="mt-2 font-display text-lg leading-snug text-paper/60" data-id="element-254" data-mp="qo2fptlr2jqq">
                        {request.body}
                      </p>
                      <span className="label-sm mt-3 block text-paper/35" data-id="element-255" data-mp="gssc51fpe4au">
                        {request.version ? `${request.version} · ` : ''}
                        Requested {request.time || request.submitted}
                        {request.completedAt ? ` · Completed ${request.completedAt}` : ' · Completed'}
                        {request.completedBy ? ` by ${request.completedBy}` : ''}
                      </span>
                    </li>)}
                </ul>
              </>}

            {outstanding.length > 0 && <div className="mt-12" data-id="element-256" data-mp="3k8pnhdplpiar">
                {deliverable.type === 'Transcript' ? <TranscriptRevisions deliverable={deliverable} data-id="element-257" data-mp="3p7suh3p55yld" /> : <VersionHistory deliverable={deliverable} data-id="element-258" data-mp="3p9n4hmyvk6ao" />}
              </div>}

          </div>
        </div>}
    </Reorder.Item>;
}